// components/sections/GoalsSection.jsx
import React, { useState, useEffect } from 'react';
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from "../ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../ui/tabs";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { RadioGroup, RadioGroupItem } from "../ui/radio-group";
import { Progress } from "../ui/progress";
import { ScrollArea } from "../ui/scroll-area";
import { Badge } from "../ui/badge";
import { format } from 'date-fns';
import { Target, Trophy, Scale, Flag, Plus, PlusCircle, XCircle, ChevronRight, Trash2, Edit, Calendar } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { useToast } from "../ui/use-toast";

const GoalsSection = ({ userPreferences, recentHistory, onUpdateGoals, user }) => {
  // Core state
  const [goals, setGoals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNewGoalDialog, setShowNewGoalDialog] = useState(false);
  const [goalToDelete, setGoalToDelete] = useState(null);
  const [quickUpdateState, setQuickUpdateState] = useState({
    goalId: null,
    showForm: false,
    value: ''
  });
  const [newGoal, setNewGoal] = useState({
    type: 'weight',
    target_value: '',
    target_date: '',
    title: '',
    description: ''
  });

  // Edit goal state
  const [editingGoal, setEditingGoal] = useState(null);
  const [showEditGoalDialog, setShowEditGoalDialog] = useState(false);
  
  // Milestone state
  const [showAddMilestoneDialog, setShowAddMilestoneDialog] = useState(false);
  const [selectedGoalForMilestone, setSelectedGoalForMilestone] = useState(null);
  const [newMilestone, setNewMilestone] = useState({
    title: '',
    description: '',
    target_value: '',
    target_date: ''
  });
  
  // Edit milestone state
  const [editingMilestone, setEditingMilestone] = useState(null);
  const [showEditMilestoneDialog, setShowEditMilestoneDialog] = useState(false);
  
  const { toast } = useToast();

  // Check if target has been met
  const checkIfTargetMet = (goal, currentValue) => {
    if (goal.type === 'weight') {
      if (goal.target_value < goal.start_value) {
        return currentValue <= goal.target_value;
      }
      return currentValue >= goal.target_value;
    }
    else if (goal.type === 'body_fat') {
      return currentValue <= goal.target_value;
    }
    return false;
  };

  // Load goals with milestones
  const loadGoals = async () => {
    setLoading(true);
    try {
      // First get all goals
      const { data: goalsData, error: goalsError } = await supabase
        .from('goals')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });

      if (goalsError) throw goalsError;

      // Then get all milestones and goal progress for each goal
      const goalsWithDetails = await Promise.all(goalsData.map(async (goal) => {
        const [milestonesResponse, progressResponse] = await Promise.all([
          supabase
            .from('milestones')
            .select('*')
            .eq('goal_id', goal.id)
            .order('target_date', { ascending: true }),
          supabase
            .from('goal_progress')
            .select('*')
            .eq('goal_id', goal.id)
            .order('recorded_at', { ascending: false })
        ]);

        return {
          ...goal,
          milestones: milestonesResponse.data || [],
          goal_progress: progressResponse.data?.sort((a, b) => 
            new Date(b.recorded_at) - new Date(a.recorded_at)
          ) || []
        };
      }));

      setGoals(goalsWithDetails);
    } catch (error) {
      console.error('Error loading goals:', error);
      toast({
        title: "Error",
        description: "Failed to load goals data",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadGoals();
  }, [user.id]);

  useEffect(() => {
    if (goals.length > 0) {
      // Reserved for future functionality
    }
  }, [goals]);

  // Core goal management functions
  const handleCreateGoal = async () => {
    try {
      const goalData = {
        user_id: user.id,
        type: newGoal.type,
        title: newGoal.title,
        description: newGoal.description,
        target_value: parseFloat(newGoal.target_value),
        target_date: newGoal.target_date,
        start_value: recentHistory?.[newGoal.type === 'weight' ? 'weight' : 'body_fat'] || 0,
        status: 'active'
      };

      const { data, error } = await supabase
        .from('goals')
        .insert([goalData])
        .select();

      if (error) throw error;

      setGoals([{ ...data[0], milestones: [], goal_progress: [] }, ...goals]);
      setShowNewGoalDialog(false);
      setNewGoal({
        type: 'weight',
        target_value: '',
        target_date: '',
        title: '',
        description: ''
      });

      toast({
        title: "Goal Created",
        description: "Your new goal has been created successfully."
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to create goal",
        variant: "destructive"
      });
    }
  };

  // Edit goal handler
  const handleEditGoal = () => {
    if (!editingGoal) return;
    
    setShowEditGoalDialog(true);
  };

  // Update goal handler
  const handleUpdateGoal = async () => {
    try {
      if (!editingGoal) return;

      const updatedGoalData = {
        title: editingGoal.title,
        description: editingGoal.description,
        target_value: parseFloat(editingGoal.target_value),
        target_date: editingGoal.target_date
      };

      const { data, error } = await supabase
        .from('goals')
        .update(updatedGoalData)
        .eq('id', editingGoal.id)
        .select();

      if (error) throw error;

      // Update local goals state
      setGoals(goals.map(goal => 
        goal.id === editingGoal.id 
          ? { ...goal, ...data[0] } 
          : goal
      ));

      setShowEditGoalDialog(false);
      setEditingGoal(null);

      toast({
        title: "Goal Updated",
        description: "Your goal has been updated successfully."
      });
    } catch (error) {
      console.error('Error updating goal:', error);
      toast({
        title: "Error",
        description: "Failed to update goal",
        variant: "destructive"
      });
    }
  };

  // Comprehensive progress update function
  const handleProgressUpdate = async (goalId, value, notes = "") => {
    try {
      const goal = goals.find(g => g.id === goalId);
      if (!goal) throw new Error('Goal not found');
  
      // Record goal progress
      const { error: progressError } = await supabase
        .from('goal_progress')
        .insert({
          goal_id: goalId,
          value: parseFloat(value),
          notes
        });
      if (progressError) throw progressError;
  
      // Update nutrition history
      const { id, created_at, ...historyWithoutId } = recentHistory || {};
      const historyData = {
        ...historyWithoutId,
        user_id: user.id,
        weight: goal.type === 'weight' ? parseFloat(value) : recentHistory?.weight,
        body_fat: goal.type === 'body_fat' ? parseFloat(value) : recentHistory?.body_fat,
        notes: `Updated from goal: ${goal.title}`,
        bmr: recentHistory?.bmr || 0,
        tdee: recentHistory?.tdee || 0,
        target_calories: recentHistory?.target_calories || 0,
        goal_type: recentHistory?.goal_type || 'maintenance',
        protein_grams: recentHistory?.protein_grams || 0,
        carbs_grams: recentHistory?.carbs_grams || 0,
        fat_grams: recentHistory?.fat_grams || 0
      };
  
      const { error: historyError } = await supabase
        .from('nutrition_history')
        .insert([historyData]);
      
      if (historyError) throw historyError;
  
      // Check if target is met
      const hasMetTarget = checkIfTargetMet(goal, parseFloat(value));
      if (hasMetTarget && goal.status !== 'completed') {
        const { error: statusError } = await supabase
          .from('goals')
          .update({ 
            status: 'completed',
            completion_date: new Date().toISOString()
          })
          .eq('id', goalId);
  
        if (statusError) throw statusError;
  
        toast({
          title: "Goal Completed! 🎉",
          description: `Congratulations! You've reached your ${goal.type} target!`
        });
      }
  
      // Refresh data
      await loadGoals();
      if (onUpdateGoals) await onUpdateGoals();
  
      // Dispatch a custom event to notify the ProfilePage component about the weight update
      if (goal.type === 'weight') {
        console.log('Dispatching weight-update event');
        window.dispatchEvent(new Event('weight-update'));
      }
  
      // Dispatch nutrition-update event (this already exists in your code)
      if (typeof window !== 'undefined') {
        window.dispatchEvent(new Event('nutrition-update'));
      }
  
      toast({
        title: "Progress Updated",
        description: "Your progress has been updated successfully."
      });
  
    } catch (error) {
      console.error('Error updating progress:', error);
      toast({
        title: "Error",
        description: "Failed to record progress",
        variant: "destructive"
      });
    }
  };
  
  // Also update the handleQuickUpdate to ensure the weight-update event is dispatched
  const handleQuickUpdate = async (e, goalId, newValue) => {
    e.preventDefault();
    if (!newValue) return;
  
    try {
      await handleProgressUpdate(goalId, parseFloat(newValue));
      setQuickUpdateState({
        goalId: null,
        showForm: false,
        value: ''
      });
    } catch (error) {
      console.error('Failed to update progress:', error);
    }
  };

  // Status update handler
  const handleStatusUpdate = async (goalId, newStatus) => {
    try {
      const { data, error } = await supabase
        .from('goals')
        .update({ 
          status: newStatus,
          completion_date: newStatus === 'completed' ? new Date().toISOString() : null
        })
        .eq('id', goalId)
        .select();

      if (error) throw error;

      setGoals(goals.map(goal => 
        goal.id === goalId ? { ...goal, ...data[0] } : goal
      ));

      toast({
        title: "Goal Updated",
        description: `Goal status updated to ${newStatus}`
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to update goal status",
        variant: "destructive"
      });
    }
  };

  // Delete goal handler
  const handleDeleteGoal = async (goalId) => {
    try {
      // Delete associated milestones first
      const { error: milestoneError } = await supabase
        .from('milestones')
        .delete()
        .eq('goal_id', goalId);

      if (milestoneError) throw milestoneError;

      // Then delete the goal
      const { error: goalError } = await supabase
        .from('goals')
        .delete()
        .eq('id', goalId);

      if (goalError) throw goalError;

      setGoals(goals.filter(goal => goal.id !== goalId));
      setGoalToDelete(null);
      
      toast({
        title: "Goal Deleted",
        description: "Your goal has been deleted successfully."
      });
    } catch (error) {
      console.error('Error deleting goal:', error);
      toast({
        title: "Error",
        description: "Failed to delete goal",
        variant: "destructive"
      });
    }
  };

  // Milestone management
  const handleAddMilestone = async () => {
    try {
      const milestone = {
        goal_id: selectedGoalForMilestone.id,
        title: newMilestone.title,
        description: newMilestone.description,
        target_value: parseFloat(newMilestone.target_value),
        target_date: newMilestone.target_date,
        completed: false
      };

      const { data, error } = await supabase
        .from('milestones')
        .insert([milestone])
        .select();

      if (error) throw error;

      setGoals(goals.map(goal => 
        goal.id === selectedGoalForMilestone.id
          ? { ...goal, milestones: [...goal.milestones, data[0]] }
          : goal
      ));

      setShowAddMilestoneDialog(false);
      setNewMilestone({
        title: '',
        description: '',
        target_value: '',
        target_date: ''
      });
      
      toast({
        title: "Milestone Added",
        description: "New milestone has been added to your goal."
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to add milestone",
        variant: "destructive"
      });
    }
  };

  // Edit milestone handler
  const handleEditMilestone = (goalId, milestone) => {
    setEditingMilestone({
      ...milestone,
      goalId // Store the parent goal ID
    });
    setShowEditMilestoneDialog(true);
  };

  // Update milestone handler
  const handleUpdateMilestone = async () => {
    try {
      if (!editingMilestone) return;

      const updatedMilestoneData = {
        title: editingMilestone.title,
        description: editingMilestone.description,
        target_value: parseFloat(editingMilestone.target_value),
        target_date: editingMilestone.target_date
      };

      const { data, error } = await supabase
        .from('milestones')
        .update(updatedMilestoneData)
        .eq('id', editingMilestone.id)
        .select();

      if (error) throw error;

      // Update local goals state with updated milestone
      setGoals(goals.map(goal => 
        goal.id === editingMilestone.goalId
          ? {
              ...goal,
              milestones: goal.milestones.map(milestone =>
                milestone.id === editingMilestone.id ? data[0] : milestone
              )
            }
          : goal
      ));

      setShowEditMilestoneDialog(false);
      setEditingMilestone(null);

      toast({
        title: "Milestone Updated",
        description: "Your milestone has been updated successfully."
      });
    } catch (error) {
      console.error('Error updating milestone:', error);
      toast({
        title: "Error",
        description: "Failed to update milestone",
        variant: "destructive"
      });
    }
  };

  const handleToggleMilestone = async (goalId, milestone) => {
    try {
      const { data, error } = await supabase
        .from('milestones')
        .update({ 
          completed: !milestone.completed,
          completion_date: !milestone.completed ? new Date().toISOString() : null
        })
        .eq('id', milestone.id)
        .select();

      if (error) throw error;

      setGoals(goals.map(goal => 
        goal.id === goalId
          ? {
              ...goal,
              milestones: goal.milestones.map(m =>
                m.id === milestone.id ? data[0] : m
              )
            }
          : goal
      ));

      toast({
        title: milestone.completed ? "Milestone Uncompleted" : "Milestone Completed",
        description: `${milestone.title} has been ${milestone.completed ? 'uncompleted' : 'completed'}.`
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to update milestone status",
        variant: "destructive"
      });
    }
  };

  // Helper functions
  const getStatusColor = (progress) => {
    if (progress >= 90) return 'bg-green-500';
    if (progress >= 60) return 'bg-yellow-500';
    if (progress >= 30) return 'bg-orange-500';
    return 'bg-red-500';
  };

  const calculateGoalProgress = (goal) => {
    if (!goal.goal_progress || goal.goal_progress.length === 0) return 0;
  
    const latestProgress = goal.goal_progress[0].value;
    const totalChange = goal.target_value - goal.start_value;
    const actualChange = latestProgress - goal.start_value;
    
    if ((goal.type === 'weight' && goal.target_value < goal.start_value) ||
        goal.type === 'body_fat') {
      // Reverse calculation for decreasing targets
      const progress = (Math.abs(actualChange) / Math.abs(totalChange)) * 100;
      return Math.min(Math.max(progress, 0), 100);
    }
    
    // Normal calculation for increasing targets
    const progress = (actualChange / totalChange) * 100;
    return Math.min(Math.max(progress, 0), 100);
  };

  const formatDateForInput = (dateString) => {
    try {
      const date = new Date(dateString);
      return date.toISOString().split('T')[0];
    } catch (e) {
      return '';
    }
  };

  // Render a goal card with milestones
  const renderGoalCard = (goal) => {
    const progress = calculateGoalProgress(goal);
    const isUpdating = quickUpdateState.goalId === goal.id;

    return (
      <Card key={goal.id}>
        <CardContent className="pt-6">
          {/* Goal Header */}
          <div className="flex items-start justify-between mb-4">
            <div className="flex items-center gap-2">
              {goal.type === 'weight' ? (
                <Scale className="h-5 w-5 text-primary" />
              ) : (
                <Target className="h-5 w-5 text-primary" />
              )}
              <div>
                <h4 className="font-semibold">{goal.title}</h4>
                <p className="text-sm text-muted-foreground">
                  Target: {goal.target_value} {goal.type === 'weight' ? 'lbs' : '%'}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Badge 
                variant={goal.status === 'completed' ? "default" : "secondary"}
                className="cursor-pointer"
                onClick={() => {
                  if (goal.status === 'active') {
                    handleStatusUpdate(goal.id, 'completed');
                  } else if (goal.status === 'completed') {
                    handleStatusUpdate(goal.id, 'active');
                  }
                }}
              >
                {goal.status === 'completed' ? "Completed" : "In Progress"}
              </Badge>
              
              {/* Edit button for goal */}
              <Button
                variant="ghost"
                size="sm"
                className="h-7 w-7 p-0 text-muted-foreground"
                onClick={() => {
                  setEditingGoal({
                    ...goal,
                    target_date: formatDateForInput(goal.target_date)
                  });
                  handleEditGoal();
                }}
              >
                <Edit className="h-4 w-4" />
              </Button>
              
              <Button
                variant="ghost"
                size="sm"
                className="h-7 w-7 p-0 text-muted-foreground hover:text-destructive"
                onClick={() => setGoalToDelete(goal.id)}
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          </div>

          {/* Progress Bar */}
          <div className="space-y-2">
            <div className="flex justify-between text-sm">
              <span>Progress</span>
              <span>{Math.round(progress)}%</span>
            </div>
            <Progress value={progress} className={getStatusColor(progress)} />
          </div>

          {/* Quick Stats */}
          <div className="mt-4 pt-4 border-t grid grid-cols-2 gap-4 text-sm">
            <div>
              <p className="text-muted-foreground">Current</p>
              <p>
                {goal.goal_progress && goal.goal_progress.length > 0
                  ? goal.goal_progress[0].value
                  : goal.start_value} {goal.type === 'weight' ? 'lbs' : '%'}
              </p>
            </div>
            <div>
              <p className="text-muted-foreground">Target Date</p>
              <p>{format(new Date(goal.target_date), 'MMM d, yyyy')}</p>
            </div>
          </div>

          {/* Milestones Section */}
          <div className="mt-4 pt-4 border-t">
            <div className="flex justify-between items-center mb-3">
              <h5 className="font-medium">Milestones</h5>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setSelectedGoalForMilestone(goal);
                  setShowAddMilestoneDialog(true);
                }}
              >
                <PlusCircle className="h-4 w-4 mr-2" />
                Add Milestone
              </Button>
            </div>
            {goal.milestones && goal.milestones.length > 0 ? (
              <div className="space-y-3">
                {goal.milestones.map((milestone) => (
                  <div
                    key={milestone.id}
                    className={`p-3 rounded-lg border ${
                      milestone.completed ? 'bg-muted/50' : ''
                    }`}
                  >
                    <div className="flex items-start justify-between">
                      <div>
                        <h4 className="font-medium">{milestone.title}</h4>
                        <p className="text-sm text-muted-foreground">
                          Target: {milestone.target_value} {goal.type === 'weight' ? 'lbs' : '%'}
                        </p>
                        {milestone.description && (
                          <p className="text-sm mt-1">{milestone.description}</p>
                        )}
                        <p className="text-xs text-muted-foreground mt-1">
                          Due: {format(new Date(milestone.target_date), 'MMM d, yyyy')}
                        </p>
                      </div>
                      <div className="flex flex-col gap-2">
                        <Button
                          variant={milestone.completed ? "outline" : "default"}
                          size="sm"
                          onClick={() => handleToggleMilestone(goal.id, milestone)}
                        >
                          {milestone.completed ? (
                            <XCircle className="h-4 w-4 mr-2" />
                          ) : (
                            <ChevronRight className="h-4 w-4 mr-2" />
                          )}
                          {milestone.completed ? "Undo" : "Complete"}
                        </Button>
                        
                        {/* Edit button for milestone */}
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleEditMilestone(goal.id, {
                            ...milestone,
                            target_date: formatDateForInput(milestone.target_date)
                          })}
                        >
                          <Edit className="h-4 w-4 mr-2" />
                          Edit
                        </Button>
                      </div>
                    </div>
                    {milestone.completed && (
                      <p className="text-xs text-muted-foreground mt-2">
                        Completed on: {format(new Date(milestone.completion_date), 'MMM d, yyyy')}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground text-center py-2">
                No milestones set for this goal yet
              </p>
            )}
          </div>

          {/* Quick Progress Update Section */}
          <div className="mt-4 pt-4 border-t">
            {!isUpdating ? (
              <Button
                variant="outline"
                className="w-full"
                onClick={() => setQuickUpdateState({
                  goalId: goal.id,
                  showForm: true,
                  value: ''
                })}
              >
                Update {goal.type === 'weight' ? 'Weight' : 'Body Fat %'}
              </Button>
            ) : (
              <form 
                onSubmit={(e) => handleQuickUpdate(e, goal.id, quickUpdateState.value)} 
                className="space-y-2"
              >
                <div className="flex gap-2">
                  <Input
                    type="number"
                    step={goal.type === 'weight' ? "0.1" : "0.1"}
                    placeholder={`Enter current ${goal.type === 'weight' ? 'weight' : 'body fat %'}`}
                    value={quickUpdateState.value}
                    onChange={(e) => setQuickUpdateState({
                      ...quickUpdateState,
                      value: e.target.value
                    })}
                    className="flex-1"
                  />
                  <Button type="submit" size="sm">
                    Save
                  </Button>
                  <Button 
                    type="button" 
                    variant="ghost" 
                    size="sm"
                    onClick={() => setQuickUpdateState({
                      goalId: null,
                      showForm: false,
                      value: ''
                    })}
                  >
                    Cancel
                  </Button>
                </div>
              </form>
            )}
          </div>

          {/* Last Updated */}
          {goal.goal_progress && goal.goal_progress.length > 0 && (
            <p className="text-xs text-muted-foreground mt-2 text-center">
              Last updated: {format(new Date(goal.goal_progress[0].recorded_at), 'MMM d, yyyy h:mm a')}
            </p>
          )}
        </CardContent>
      </Card>
    );
  };

  if (loading) {
    return <div className="flex justify-center items-center py-8">Loading goals...</div>;
  }

  return (
    <Tabs defaultValue="active" className="w-full space-y-6">
      <div className="flex justify-between items-center">
        <TabsList>
          <TabsTrigger value="active">Active Goals</TabsTrigger>
          <TabsTrigger value="completed">Completed</TabsTrigger>
        </TabsList>
        <Button onClick={() => setShowNewGoalDialog(true)}>
          <Plus className="h-4 w-4 mr-2" /> Add Goal
        </Button>
      </div>

      {/* Active Goals Tab */}
      <TabsContent value="active" className="space-y-6">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Flag className="h-5 w-5" />
              Active Goals
            </CardTitle>
            <CardDescription>Your current fitness and nutrition goals</CardDescription>
          </CardHeader>
          <CardContent>
            <ScrollArea className="h-[400px] pr-4">
              <div className="space-y-4">
                {goals
                  .filter(goal => goal.status === 'active')
                  .map(renderGoalCard)}
                {goals.filter(goal => goal.status === 'active').length === 0 && (
                  <div className="text-center py-8 text-muted-foreground">
                    No active goals. Click "Add Goal" to create one!
                  </div>
                )}
              </div>
            </ScrollArea>
          </CardContent>
        </Card>
      </TabsContent>

      {/* Completed Goals Tab */}
      <TabsContent value="completed">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Trophy className="h-5 w-5" />
              Completed Goals
            </CardTitle>
            <CardDescription>Celebrate your achievements</CardDescription>
          </CardHeader>
          <CardContent>
            <ScrollArea className="h-[400px] pr-4">
              <div className="space-y-4">
                {goals
                  .filter(goal => goal.status === 'completed')
                  .map(renderGoalCard)}
                {goals.filter(goal => goal.status === 'completed').length === 0 && (
                  <div className="text-center py-8 text-muted-foreground">
                    <Trophy className="h-12 w-12 mx-auto mb-4 opacity-50" />
                    <p>No completed goals yet. Keep pushing!</p>
                  </div>
                )}
              </div>
            </ScrollArea>
          </CardContent>
        </Card>
      </TabsContent>

      {/* New Goal Dialog */}
      <Dialog open={showNewGoalDialog} onOpenChange={setShowNewGoalDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create New Goal</DialogTitle>
            <DialogDescription>
              Set a new fitness or nutrition goal to track your progress
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Goal Type</Label>
              <RadioGroup
                value={newGoal.type}
                onValueChange={(value) => setNewGoal({ ...newGoal, type: value })}
              >
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="weight" id="weight" />
                  <Label htmlFor="weight">Weight Goal</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="body_fat" id="body_fat" />
                  <Label htmlFor="body_fat">Body Fat % Goal</Label>
                </div>
              </RadioGroup>
            </div>

            <div className="space-y-2">
              <Label>Title</Label>
              <Input
                value={newGoal.title}
                onChange={(e) => setNewGoal({ ...newGoal, title: e.target.value })}
                placeholder="e.g., Summer Cut Goal"
              />
            </div>

            <div className="space-y-2">
              <Label>Target Value</Label>
              <Input
                type="number"
                step="0.1"
                value={newGoal.target_value}
                onChange={(e) => setNewGoal({ ...newGoal, target_value: e.target.value })}
                placeholder={newGoal.type === 'weight' ? 'Target weight in lbs' : 'Target body fat %'}
              />
            </div>

            <div className="space-y-2">
              <Label>Target Date</Label>
              <Input
                type="date"
                value={newGoal.target_date}
                onChange={(e) => setNewGoal({ ...newGoal, target_date: e.target.value })}
              />
            </div>

            <div className="space-y-2">
              <Label>Description (Optional)</Label>
              <Input
                value={newGoal.description}
                onChange={(e) => setNewGoal({ ...newGoal, description: e.target.value })}
                placeholder="Brief description of your goal"
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowNewGoalDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleCreateGoal}>
              Create Goal
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Goal Dialog */}
      <Dialog open={showEditGoalDialog} onOpenChange={setShowEditGoalDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Goal</DialogTitle>
            <DialogDescription>
              Update your goal details
            </DialogDescription>
          </DialogHeader>

          {editingGoal && (
            <div className="space-y-4">
              <div className="space-y-2">
                <Label>Title</Label>
                <Input
                  value={editingGoal.title}
                  onChange={(e) => setEditingGoal({ ...editingGoal, title: e.target.value })}
                  placeholder="Goal title"
                />
              </div>

              <div className="space-y-2">
                <Label>Target Value ({editingGoal.type === 'weight' ? 'lbs' : '%'})</Label>
                <Input
                  type="number"
                  step="0.1"
                  value={editingGoal.target_value}
                  onChange={(e) => setEditingGoal({ ...editingGoal, target_value: e.target.value })}
                  placeholder={`Target ${editingGoal.type === 'weight' ? 'weight' : 'body fat %'}`}
                />
              </div>

              <div className="space-y-2">
                <Label>Target Date</Label>
                <Input
                  type="date"
                  value={editingGoal.target_date}
                  onChange={(e) => setEditingGoal({ ...editingGoal, target_date: e.target.value })}
                />
              </div>

              <div className="space-y-2">
                <Label>Description (Optional)</Label>
                <Input
                  value={editingGoal.description}
                  onChange={(e) => setEditingGoal({ ...editingGoal, description: e.target.value })}
                  placeholder="Goal description"
                />
              </div>
            </div>
          )}

          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => {
                setShowEditGoalDialog(false);
                setEditingGoal(null);
              }}
            >
              Cancel
            </Button>
            <Button onClick={handleUpdateGoal}>
              Update Goal
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Milestone Dialog */}
      <Dialog open={showAddMilestoneDialog} onOpenChange={setShowAddMilestoneDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Milestone</DialogTitle>
            <DialogDescription>
              Break down your goal into smaller, manageable milestones
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Title</Label>
              <Input
                value={newMilestone.title}
                onChange={(e) => setNewMilestone({ ...newMilestone, title: e.target.value })}
                placeholder="e.g., First 5 pounds"
              />
            </div>

            <div className="space-y-2">
              <Label>Target Value</Label>
              <Input
                type="number"
                step="0.1"
                value={newMilestone.target_value}
                onChange={(e) => setNewMilestone({ ...newMilestone, target_value: e.target.value })}
                placeholder="Milestone target value"
              />
            </div>

            <div className="space-y-2">
              <Label>Target Date</Label>
              <Input
                type="date"
                value={newMilestone.target_date}
                onChange={(e) => setNewMilestone({ ...newMilestone, target_date: e.target.value })}
              />
            </div>

            <div className="space-y-2">
              <Label>Description (Optional)</Label>
              <Input
                value={newMilestone.description}
                onChange={(e) => setNewMilestone({ ...newMilestone, description: e.target.value })}
                placeholder="Brief description of this milestone"
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddMilestoneDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleAddMilestone}>
              Add Milestone
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Milestone Dialog */}
      <Dialog open={showEditMilestoneDialog} onOpenChange={setShowEditMilestoneDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Milestone</DialogTitle>
            <DialogDescription>
              Update your milestone details
            </DialogDescription>
          </DialogHeader>

          {editingMilestone && (
            <div className="space-y-4">
              <div className="space-y-2">
                <Label>Title</Label>
                <Input
                  value={editingMilestone.title}
                  onChange={(e) => setEditingMilestone({ ...editingMilestone, title: e.target.value })}
                  placeholder="Milestone title"
                />
              </div>

              <div className="space-y-2">
                <Label>Target Value</Label>
                <Input
                  type="number"
                  step="0.1"
                  value={editingMilestone.target_value}
                  onChange={(e) => setEditingMilestone({ ...editingMilestone, target_value: e.target.value })}
                  placeholder="Target value"
                />
              </div>

              <div className="space-y-2">
                <Label>Target Date</Label>
                <Input
                  type="date"
                  value={editingMilestone.target_date}
                  onChange={(e) => setEditingMilestone({ ...editingMilestone, target_date: e.target.value })}
                />
              </div>

              <div className="space-y-2">
                <Label>Description (Optional)</Label>
                <Input
                  value={editingMilestone.description}
                  onChange={(e) => setEditingMilestone({ ...editingMilestone, description: e.target.value })}
                  placeholder="Milestone description"
                />
              </div>
            </div>
          )}

          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => {
                setShowEditMilestoneDialog(false);
                setEditingMilestone(null);
              }}
            >
              Cancel
            </Button>
            <Button onClick={handleUpdateMilestone}>
              Update Milestone
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Goal Dialog */}
      <Dialog open={goalToDelete !== null} onOpenChange={(open) => !open && setGoalToDelete(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Goal</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete this goal? This action cannot be undone.
              All associated milestones will also be deleted.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setGoalToDelete(null)}>
              Cancel
            </Button>
            <Button 
              variant="destructive" 
              onClick={() => handleDeleteGoal(goalToDelete)}
            >
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Tabs>
  );
};

export default GoalsSection;